/**
 * DesignReviewWorkspace — 5-tab UI for the Design Review module.
 * Wave DR (Wave-3). Backed by /api/projects/:id/design-review/*.
 *
 *   Tab 1: Upload — drag-drop drawings + Extract button.
 *   Tab 2: Validate — parameter × standard × verdict compliance table.
 *   Tab 3: Optimize — quality-gated suggestions, accept toggle, ROI panel.
 *   Tab 4: Redesign — before/after comparison + download PDF + Excel.
 *   Tab 5: 3D BIM & Rerun — Wave-3: preview affected stages, live
 *          orchestrator progress, IFC download, Revit .rvt upload.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import {
  Upload, FileSearch, Sparkles, GitCompareArrows, Loader2, CheckCircle2,
  AlertCircle, XCircle, Info, Download, Trash2, FileText, FileSpreadsheet,
  Shield, ArrowRight, ChevronRight, RefreshCcw, Boxes, Cog, RotateCcw,
  Cloud
} from "lucide-react";

const COL = {
  bg:"var(--pf-bg,#020617)", surface:"var(--pf-surface,#0f1729)",
  border:"var(--pf-border,#1e293b)", text:"var(--pf-text,#e2e8f0)",
  dim:"var(--pf-text-muted,#94a3b8)", spark:"var(--pf-spark,#22d3ee)",
  amber:"#f59e0b", green:"#22c55e", red:"#ef4444",
};
const mono = { fontFamily:"'JetBrains Mono','Fira Code',monospace" };

const TABS = [
  { key:"upload",    label:"Upload",    Icon: Upload },
  { key:"validate",  label:"Validate",  Icon: FileSearch },
  { key:"optimize",  label:"Optimize",  Icon: Sparkles },
  { key:"redesign",  label:"Redesign",  Icon: GitCompareArrows },
  { key:"bim",       label:"3D BIM & Rerun", Icon: Boxes },
];

export default function DesignReviewWorkspace({ projectId, project }) {
  const [tab, setTab] = useState("upload");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [edo, setEdo] = useState(null);
  const [items, setItems] = useState([]);
  const [score, setScore] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [accepted, setAccepted] = useState(new Set());
  const [redesign, setRedesign] = useState(null);
  const [impactPreview, setImpactPreview] = useState(null);
  const [orchestrator, setOrchestrator] = useState(null);
  const [rvtJobs, setRvtJobs] = useState([]);
  const [restoredMsg, setRestoredMsg] = useState("");
  const inputRef = useRef(null);
  const rvtInputRef = useRef(null);

  const country = project?.country || project?.industry_config?.country || "IN";
  const state = project?.state || null;
  const city = project?.city || null;
  const bt = project?.building_type || project?.re_details?.building_type || "residential";

  // Load whatever the project already has cached
  const loadState = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/design-review/gap-report`);
      const d = r.data || {};
      if (d.edo && Object.keys(d.edo).length) setEdo(d.edo);
      if (d.items) setItems(d.items);
      if (d.score) setScore(d.score);
      if (d.suggestions) setSuggestions(d.suggestions);
      if (d.accepted) setAccepted(new Set(d.accepted));
    } catch { /* no prior state */ }
    try {
      const r2 = await api.get(`/projects/${projectId}/design-review/comparison`);
      if (r2.data) setRedesign(r2.data);
    } catch { /* not yet redesigned */ }
  }, [projectId]);
  useEffect(() => { loadState(); }, [loadState]);

  // ─── Upload ──────────────────────────────────────────
  const onDrop = async (files) => {
    setErr(""); setBusy("upload");
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await api.post(`/projects/${projectId}/design-review/upload`, fd,
                                 { headers:{ "Content-Type":"multipart/form-data" } });
      setUploads((r.data?.uploaded) || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const onExtract = async () => {
    setErr(""); setBusy("extract");
    try {
      const r = await api.post(`/projects/${projectId}/design-review/ingest`,
                                { country, building_type: bt, state, city });
      setEdo(r.data?.edo);
      setTab("validate");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const onValidate = async () => {
    setErr(""); setBusy("validate");
    try {
      const r = await api.post(`/projects/${projectId}/design-review/validate`);
      setItems(r.data?.items || []);
      setScore(r.data?.score || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const onOptimize = async () => {
    setErr(""); setBusy("optimize");
    try {
      const r = await api.post(`/projects/${projectId}/design-review/optimize`);
      const sugg = r.data?.suggestions || [];
      setSuggestions(sugg);
      setTab("optimize");
      // Defensive: also refresh from gap-report so the UI always mirrors
      // what the backend persisted (guards against any race with loadState).
      if (sugg.length === 0) {
        try {
          const g = await api.get(`/projects/${projectId}/design-review/gap-report`);
          if (g.data?.suggestions?.length) setSuggestions(g.data.suggestions);
        } catch { /* ignore */ }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const toggleAccept = (id) => {
    setAccepted(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const onSaveAccepted = async () => {
    setBusy("accept");
    try {
      await api.post(`/projects/${projectId}/design-review/accept`,
                       { accepted_ids: [...accepted] });
    } finally { setBusy(""); }
  };

  const onRedesign = async () => {
    setErr(""); setBusy("redesign");
    try {
      await onSaveAccepted();
      const r = await api.post(`/projects/${projectId}/design-review/redesign`);
      setRedesign(r.data);
      setTab("redesign");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const onDownload = (fmt) => {
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}` +
                `/api/projects/${projectId}/design-review/download/${fmt}`;
    window.open(url, "_blank");
  };

  // ─── Wave-3: BIM & Rerun ─────────────────────────────
  const loadImpactPreview = useCallback(async () => {
    try {
      const r = await api.get(
        `/projects/${projectId}/design-review/impact-preview`);
      setImpactPreview(r.data || null);
    } catch { /* not ready */ }
  }, [projectId]);

  const loadOrchestrator = useCallback(async () => {
    try {
      const r = await api.get(
        `/projects/${projectId}/design-review/orchestrator-status`);
      setOrchestrator(r.data || null);
    } catch { /* no run yet */ }
  }, [projectId]);

  // Poll orchestrator every 5 s while in-progress/scheduled AND user is on BIM tab.
  useEffect(() => {
    if (tab !== "bim") return;
    loadImpactPreview();
    loadOrchestrator();
    const iv = setInterval(() => {
      const s = orchestrator?.status;
      if (s === "in_progress" || s === "scheduled") loadOrchestrator();
    }, 5000);
    return () => clearInterval(iv);
  }, [tab, orchestrator?.status, loadImpactPreview, loadOrchestrator]);

  const onRestoreBaseline = async () => {
    setErr(""); setBusy("restore"); setRestoredMsg("");
    try {
      const r = await api.post(
        `/projects/${projectId}/design-review/restore-baseline`);
      const msg = r.data?.restored
        ? "Baseline restored — master plan reverted to pre-redesign state."
        : r.data?.error || "";
      setRestoredMsg(msg);
      await loadOrchestrator();
      // Auto-hide the success/info banner after 8s to avoid staleness
      if (msg) setTimeout(() => setRestoredMsg(""), 8000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const onRvtUpload = async (files) => {
    if (!files || files.length === 0) return;
    setErr(""); setBusy("rvt");
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const r = await api.post(
        `/projects/${projectId}/design-review/rvt-ingest`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRvtJobs(r.data?.submitted || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(""); }
  };

  const onRvtPoll = async (urn) => {
    try {
      const r = await api.get(
        `/projects/${projectId}/design-review/rvt-status/${urn}`);
      setRvtJobs(prev => prev.map(j => j.urn === urn
        ? { ...j, ...r.data } : j));
    } catch { /* ignore */ }
  };

  const totalSaving = useMemo(
    () => (suggestions || []).filter(s => accepted.has(s.id))
                              .reduce((s, x) => s + (x.saving_amount || 0), 0),
    [suggestions, accepted]);

  return (
    <div data-testid="design-review-workspace"
         style={{ color: COL.text, ...mono, height: "100%",
                   display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ borderBottom: `1px solid ${COL.border}`,
                       padding: "12px 20px", display: "flex",
                       alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <Shield size={18} style={{ color: COL.spark }} />
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
          DESIGN REVIEW · VALIDATE · OPTIMIZE
        </h2>
        <span style={{ color: COL.dim, fontSize: 11 }}>
          {country}{state ? ` · ${state}` : ""}{city ? ` · ${city}` : ""} · {bt}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {TABS.map(t => (
            <button key={t.key}
                     data-testid={`dr-tab-${t.key}`}
                     onClick={() => setTab(t.key)}
                     style={tabBtn(tab === t.key)}>
              <t.Icon size={12} style={{ marginRight: 4,
                                            verticalAlign: -1 }} />
              {t.label}
            </button>
          ))}
          <button data-testid="dr-refresh" onClick={loadState}
                   title="Reload cached state" style={ghostBtn}>
            <RefreshCcw size={12} />
          </button>
        </div>
      </div>

      {err && (
        <div style={{ background: COL.red, color: "white", padding: 10,
                        fontSize: 12 }}>
          <AlertCircle size={12} style={{ marginRight: 6,
                                             verticalAlign: -1 }} /> {err}
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
        {tab === "upload" && (
          <UploadPane inputRef={inputRef} onDrop={onDrop} busy={busy}
                        uploads={uploads} onExtract={onExtract} edo={edo}
                        projectId={projectId} />
        )}
        {tab === "validate" && (
          <ValidatePane edo={edo} items={items} score={score}
                          onValidate={onValidate} busy={busy}
                          onOptimize={onOptimize} />
        )}
        {tab === "optimize" && (
          <OptimizePane suggestions={suggestions} accepted={accepted}
                          toggleAccept={toggleAccept}
                          totalSaving={totalSaving}
                          onRedesign={onRedesign} busy={busy} />
        )}
        {tab === "redesign" && (
          <RedesignPane redesign={redesign} onDownload={onDownload}
                        projectId={projectId} />
        )}
        {tab === "bim" && (
          <BimPane projectId={projectId}
                    accepted={accepted}
                    suggestions={suggestions}
                    impactPreview={impactPreview}
                    orchestrator={orchestrator}
                    onRefresh={loadOrchestrator}
                    onRestoreBaseline={onRestoreBaseline}
                    onDownload={onDownload}
                    rvtInputRef={rvtInputRef}
                    onRvtUpload={onRvtUpload}
                    onRvtPoll={onRvtPoll}
                    rvtJobs={rvtJobs}
                    restoredMsg={restoredMsg}
                    busy={busy} />
        )}
      </div>
    </div>
  );
}

/* ── Tab panes ─────────────────────────────────────────── */

function UploadPane({ inputRef, onDrop, busy, uploads, onExtract, edo, projectId }) {
  return (
    <div>
      <div data-testid="dr-drop-zone"
            onDrop={e => { e.preventDefault();
                            onDrop([...e.dataTransfer.files]); }}
            onDragOver={e => e.preventDefault()}
            style={dropStyle}>
        <Upload size={38} style={{ color: COL.spark, opacity: 0.7 }} />
        <div style={{ marginTop: 14, fontWeight: 700, fontSize: 14 }}>
          Drop drawings, area statements or BOQs here
        </div>
        <div style={{ marginTop: 6, color: COL.dim, fontSize: 11,
                        maxWidth: 480, textAlign: "center" }}>
          Supported: PDF · PNG · JPG · DWG · IFC · XLSX · CSV.
          Multiple files welcome — the engine merges what it can read.
        </div>
        <input ref={inputRef} type="file" multiple hidden
                data-testid="dr-file-input"
                onChange={e => onDrop([...e.target.files])} />
        <button data-testid="dr-file-picker" style={primaryBtn}
                 onClick={() => inputRef.current?.click()}>
          Choose files
        </button>
      </div>
      {uploads.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={sectionTitle}>Uploaded ({uploads.length})</div>
          {uploads.map(u => (
            <div key={u.filename} style={rowStyle}>
              <FileText size={14} style={{ color: COL.spark }} />
              <span style={{ flex: 1 }}>{u.filename}</span>
              <span style={{ color: COL.dim, fontSize: 11 }}>
                {Math.round((u.size || 0) / 1024)} KB
              </span>
              <span style={statusPill("uploaded")}>{u.status}</span>
            </div>
          ))}
          <button data-testid="dr-extract" style={{ ...primaryBtn, marginTop: 16 }}
                   disabled={!!busy} onClick={onExtract}>
            {busy === "extract" ? <Loader2 className="spin" size={13} /> :
              <Sparkles size={13} />}
            &nbsp;Extract parameters
          </button>
        </div>
      )}
      {edo && (
        <div style={{ marginTop: 24 }}>
          <div style={sectionTitle}>
            Extraction confidence:&nbsp;
            <b style={{ color: (edo._meta?.extraction_confidence_pct || 0) >= 60
                        ? COL.green : COL.amber }}>
              {edo._meta?.extraction_confidence_pct || 0}%
            </b>
          </div>
          <div style={{ color: COL.dim, fontSize: 11, marginBottom: 8 }}>
            {Object.entries(edo).filter(([k, v]) => !k.startsWith("_") &&
              typeof v === "object" && Object.keys(v || {}).length > 0)
              .map(([k]) => k.replace(/^./, m => m.toUpperCase())).join(" · ")}
          </div>
          {/* Editable EDO — RG6 · Wave-4 */}
          <EdoEditor edo={edo} projectId={projectId} />
          <button data-testid="dr-go-validate"
                   style={{ ...secondaryBtn, marginTop: 12 }}
                   onClick={() => window.dispatchEvent(new CustomEvent(
                     "dr-tab-change", { detail: "validate" }))}>
            Continue to Validate <ArrowRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

/** EdoEditor — inline editor for extracted design parameters (RG6). */
function EdoEditor({ edo, projectId }) {
  const [draft, setDraft] = useState(() => copyClean(edo));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [dirty, setDirty] = useState(false);

  const onChange = (section, field, value) => {
    setDraft(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: value }
    }));
    setDirty(true);
  };
  const onSave = async () => {
    setSaving(true); setMsg("");
    try {
      // Build a minimal diff patch (only fields user changed)
      const patch = {};
      Object.keys(draft).forEach(section => {
        if (section.startsWith("_") ||
            typeof draft[section] !== "object" ||
            Array.isArray(draft[section])) return;
        Object.keys(draft[section]).forEach(field => {
          const orig = (edo[section] || {})[field];
          const cur = draft[section][field];
          if (String(orig ?? "") !== String(cur ?? "")) {
            patch[section] = patch[section] || {};
            patch[section][field] = _coerce(cur);
          }
        });
      });
      if (Object.keys(patch).length === 0) {
        setMsg("Nothing to save.");
      } else {
        await api.patch(`/projects/${projectId}/design-review/edo`, patch);
        setMsg(`Saved ${Object.values(patch)
          .reduce((sum, s) => sum + Object.keys(s).length, 0)} change(s).`);
        setDirty(false);
      }
      setTimeout(() => setMsg(""), 5000);
    } catch (e) {
      setMsg("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const sections = Object.keys(draft)
    .filter(k => !k.startsWith("_") && typeof draft[k] === "object"
                  && !Array.isArray(draft[k])
                  && Object.keys(draft[k]).length > 0);

  return (
    <div data-testid="dr-edo-editor"
         style={{ marginTop: 16, background: COL.surface,
                    border: `1px solid ${COL.border}`, borderRadius: 6,
                    padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COL.text }}>
          Extracted parameters (edit any value to correct extraction)
        </div>
        <div>
          {dirty && (
            <button data-testid="dr-edo-save" onClick={onSave}
                     disabled={saving}
                     style={{ ...primaryBtn, padding: "5px 12px",
                              fontSize: 11 }}>
              {saving ? <Loader2 size={10} className="animate-spin" /> : null}
              &nbsp;Save changes
            </button>
          )}
          {msg && (
            <span data-testid="dr-edo-save-msg"
                   style={{ marginLeft: 10, fontSize: 11,
                            color: msg.startsWith("Saved") ? COL.green
                                    : msg.startsWith("Save failed") ? COL.red
                                    : COL.dim }}>
              {msg}
            </span>
          )}
        </div>
      </div>
      {sections.map(section => (
        <div key={section} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase",
                          fontWeight: 700, color: COL.dim,
                          marginBottom: 4 }}>{section}</div>
          <div style={{ display: "grid", gridTemplateColumns:
                          "repeat(auto-fill, minmax(240px, 1fr))",
                          gap: 6 }}>
            {Object.entries(draft[section] || {})
              .filter(([k, v]) => !k.startsWith("_") &&
                typeof v !== "object")
              .map(([k, v]) => (
                <label key={k} style={{ display: "flex",
                                          flexDirection: "column",
                                          fontSize: 10, color: COL.dim }}>
                  {k}
                  <input data-testid={`dr-edo-input-${section}-${k}`}
                          type="text"
                          value={v ?? ""}
                          onChange={e => onChange(section, k, e.target.value)}
                          style={{ padding: "4px 6px", background: COL.bg,
                                    border: `1px solid ${COL.border}`,
                                    borderRadius: 3, color: COL.text,
                                    fontSize: 11 }} />
                </label>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function copyClean(edo) {
  const out = {};
  Object.entries(edo || {}).forEach(([k, v]) => {
    if (typeof v === "object" && v && !Array.isArray(v)) out[k] = { ...v };
    else out[k] = v;
  });
  return out;
}

function _coerce(v) {
  // Try numeric first
  if (v === "" || v == null) return null;
  const s = String(v).trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  if (s === "true" || s === "false") return s === "true";
  return s;
}

function ValidatePane({ edo, items, score, onValidate, busy, onOptimize }) {
  const [filter, setFilter] = useState("all"); // all | fail | critical
  const filtered = items.filter(i => {
    if (filter === "all") return true;
    if (filter === "fail") return i.status === "FAIL";
    return i.severity === filter;
  });
  const groups = {};
  filtered.forEach(i => {
    groups[i.discipline] = groups[i.discipline] || [];
    groups[i.discipline].push(i);
  });
  return (
    <div>
      {!edo && (
        <EmptyCTA text="Run the Upload → Extract step first."
                    testid="validate-empty" />
      )}
      {edo && (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center",
                          flexWrap: "wrap", marginBottom: 14 }}>
            <button data-testid="dr-validate" onClick={onValidate}
                     style={primaryBtn} disabled={busy === "validate"}>
              {busy === "validate" ? <Loader2 className="spin" size={13} /> :
                <FileSearch size={13} />}
              &nbsp;Validate against codes
            </button>
            <span style={{ color: COL.dim, fontSize: 11 }}>
              {items.length} checks
            </span>
            {score && (
              <>
                <span style={scorePill(score.score_pct)}
                        data-testid="dr-score">
                  {score.score_pct}% compliance
                </span>
                <span style={{ color: COL.dim, fontSize: 11 }}>
                  {(score.by_status?.PASS || 0)} PASS · &nbsp;
                  {(score.by_status?.FAIL || 0)} FAIL
                </span>
              </>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
              {["all", "fail", "critical", "major", "minor"].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                         data-testid={`dr-filter-${f}`}
                         style={filterBtn(filter === f)}>{f}</button>
              ))}
            </div>
          </div>

          {items.length > 0 && Object.entries(groups).map(([disc, list]) => (
            <div key={disc} style={{ marginBottom: 20 }}>
              <div style={sectionTitle}>{disc.toUpperCase()} · {list.length}</div>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Check</th>
                    <th style={thStyle}>Actual</th>
                    <th style={thStyle}>Required</th>
                    <th style={thStyle}>Standard</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Sev</th>
                    <th style={thStyle}>Remediation</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((it, idx) => (
                    <tr key={idx} data-testid={`dr-row-${it.parameter}`}
                        style={{ background: idx % 2 ? "rgba(255,255,255,.02)"
                                                          : "transparent" }}>
                      <td style={tdStyle}>
                        <b>{it.check_name}</b>
                        <div style={{ color: COL.dim, fontSize: 10 }}>
                          {it.parameter}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color:
                            it.status === "PASS" ? COL.green :
                            it.status === "FAIL" ? COL.red : COL.dim }}>
                        {fmtVal(it.actual_value)} <small>{it.unit}</small>
                      </td>
                      <td style={{ ...tdStyle, color: COL.dim }}>
                        {fmtVal(it.required_value)} <small>{it.unit}</small>
                      </td>
                      <td style={{ ...tdStyle, color: COL.dim, fontSize: 10 }}>
                        {it.standard_ref}
                      </td>
                      <td style={{ ...tdStyle }}>
                        <span style={statusPill(it.status)}>{it.status}</span>
                      </td>
                      <td style={{ ...tdStyle }}>
                        <span style={severityPill(it.severity)}>{it.severity}</span>
                      </td>
                      <td style={{ ...tdStyle, color: COL.dim, fontSize: 11 }}>
                        {it.status !== "PASS" ? it.remediation : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {items.length > 0 && (
            <button data-testid="dr-go-optimize" onClick={onOptimize}
                     style={secondaryBtn}
                     disabled={busy === "optimize"}>
              {busy === "optimize" ? <Loader2 className="spin" size={13} /> :
                <Sparkles size={13} />}
              &nbsp;Continue to Optimize <ChevronRight size={12} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function OptimizePane({ suggestions, accepted, toggleAccept, totalSaving,
                          onRedesign, busy }) {
  if (!suggestions.length) return (
    <EmptyCTA text="Run Validate → Continue to Optimize."
                testid="optimize-empty" />);
  // YG3 — group by tier: free / low_cost / medium / high_investment
  const TIERS = [
    { key: "free",             label: "Free (₹0 CapEx)",         color: COL.green },
    { key: "low_cost",         label: "Low-cost (payback < 12 mo)", color: COL.spark },
    { key: "medium",           label: "Medium (payback 1-3 yr)",   color: COL.amber },
    { key: "high_investment",  label: "High investment (> 3 yr payback)", color: COL.red },
  ];
  const byTier = {};
  TIERS.forEach(t => byTier[t.key] = []);
  suggestions.forEach(s => {
    const t = s.tier || (s.payback_months === 0 ? "free"
                             : s.payback_months <= 12 ? "low_cost"
                             : s.payback_months <= 36 ? "medium"
                             : "high_investment");
    (byTier[t] = byTier[t] || []).push(s);
  });
  return (
    <div>
      <div style={{ ...sectionTitle, display: "flex",
                       justifyContent: "space-between" }}>
        <span>Quality-preserving suggestions · {suggestions.length}</span>
        <span data-testid="dr-total-saving" style={{ color: COL.green }}>
          Accepted total ₹{totalSaving.toLocaleString()}
        </span>
      </div>
      {TIERS.map(t => {
        const list = byTier[t.key] || [];
        if (!list.length) return null;
        return (
          <div key={t.key} data-testid={`dr-tier-${t.key}`}
                style={{ marginBottom: 20 }}>
            <div style={{ display: "inline-flex", alignItems: "center",
                            gap: 8, padding: "4px 10px",
                            background: COL.surface, borderLeft:
                              `3px solid ${t.color}`, borderRadius: 3,
                            fontSize: 11, fontWeight: 700, color: COL.text,
                            marginBottom: 10 }}>
              {t.label} <span style={{ color: COL.dim,
                                          fontWeight: 400 }}>
                ({list.length})
              </span>
            </div>
            {list.map(s => (
              <div key={s.id} data-testid={`dr-sug-${s.id}`}
                   style={cardStyle}>
                <div style={{ display: "flex", gap: 12,
                                alignItems: "center", marginBottom: 6 }}>
                  <label style={{ display: "flex", alignItems: "center",
                                    cursor: "pointer" }}>
                    <input type="checkbox"
                            data-testid={`dr-accept-${s.id}`}
                            checked={accepted.has(s.id)}
                            onChange={() => toggleAccept(s.id)}
                            style={{ marginRight: 8 }} />
                    <b style={{ color: COL.spark }}>{s.id}</b>
                  </label>
                  <span style={{ color: COL.text }}>{s.title}</span>
                  <span style={categoryPill(s.category)}>{s.category}</span>
                  <span style={{ marginLeft: "auto", color: COL.green,
                                   fontWeight: 700 }}>
                    {s.saving_unit} {(s.saving_amount || 0).toLocaleString()}
                    <small style={{ color: COL.dim, marginLeft: 6 }}>
                      {s.saving_pct}%
                    </small>
                  </span>
                </div>
                <div style={{ color: COL.dim, fontSize: 12,
                                marginBottom: 6 }}>
                  {s.description}
                </div>
                <div style={{ display: "flex", gap: 18, fontSize: 11,
                                color: COL.dim, flexWrap: "wrap" }}>
                  <span>Before: <b style={{ color: COL.text }}>
                    {fmtVal(s.before_value)}
                  </b></span>
                  <span>After: <b style={{ color: COL.text }}>
                    {fmtVal(s.after_value)}
                  </b></span>
                  <span>Payback: <b>{s.payback_months || 0} mo</b></span>
                  <span>Risk: <b style={{ color:
                        s.risk_level === "low" ? COL.green :
                        s.risk_level === "medium" ? COL.amber : COL.red }}>
                    {s.risk_level}
                  </b></span>
                  <span style={{ color: COL.spark }}>
                    <Shield size={10} style={{ verticalAlign: -1,
                                                  marginRight: 3 }} />
                    {s.impact_on_quality}
                  </span>
                </div>
              </div>
            ))}
          </div>
        );
      })}
      <button data-testid="dr-redesign" style={primaryBtn}
               disabled={!accepted.size || busy === "redesign"}
               onClick={onRedesign}>
        {busy === "redesign" ? <Loader2 className="spin" size={13} /> :
          <GitCompareArrows size={13} />}
        &nbsp;Redesign with {accepted.size} accepted
      </button>
    </div>
  );
}

function RedesignPane({ redesign, onDownload, projectId }) {
  if (!redesign) return (
    <EmptyCTA text="Accept suggestions and hit Redesign."
                testid="redesign-empty" />);
  const B = redesign.before || {};
  const A = redesign.after || {};
  const D = redesign.delta || {};
  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
        <button data-testid="dr-download-pdf" style={secondaryBtn}
                 onClick={() => onDownload("pdf")}>
          <FileText size={13} />&nbsp;Download PDF report
        </button>
        <button data-testid="dr-download-docx" style={secondaryBtn}
                 onClick={() => onDownload("docx")}>
          <FileText size={13} />&nbsp;Download DOCX (Word)
        </button>
        <button data-testid="dr-download-xlsx" style={secondaryBtn}
                 onClick={() => onDownload("xlsx")}>
          <FileSpreadsheet size={13} />&nbsp;Download Excel
        </button>
        <button data-testid="dr-download-ifc-redesign" style={secondaryBtn}
                 onClick={() => onDownload("ifc")}>
          <Boxes size={13} />&nbsp;Download IFC (3D BIM)
        </button>
      </div>

      <div style={{ display: "grid",
                       gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        {[
          { key: "cost", lbl: "Total cost", b: B.total_cost, a: A.total_cost,
              d: D.cost_saving_pct, u: "₹", better_less: true },
          { key: "area", lbl: "Total area", b: B.total_area_sqm,
              a: A.total_area_sqm, d: D.area_efficiency_gain_pct, u: "sqm",
              better_less: false },
          { key: "epi", lbl: "EPI (energy)", b: B.epi_kWh_sqm_year,
              a: A.epi_kWh_sqm_year, d: D.energy_reduction_pct,
              u: "kWh/m²·yr", better_less: true },
          { key: "steel", lbl: "Steel qty", b: B.steel_kg, a: A.steel_kg,
              d: D.steel_reduction_pct, u: "kg", better_less: true },
          { key: "concrete", lbl: "Concrete qty", b: B.concrete_m3,
              a: A.concrete_m3, d: D.concrete_reduction_pct, u: "m³",
              better_less: true },
          { key: "compliance", lbl: "Compliance score", b: "—",
              a: "+" + (D.compliance_score_gain_pct || 0),
              d: D.compliance_score_gain_pct, u: "pp", better_less: false },
        ].map(m => {
          const delta_pos = m.better_less
              ? (Number(m.a) < Number(m.b))
              : (Number(m.a) > Number(m.b));
          return (
            <div key={m.key} data-testid={`dr-metric-${m.key}`}
                  style={cardStyle}>
              <div style={{ color: COL.dim, fontSize: 11 }}>{m.lbl}</div>
              <div style={{ display: "flex", alignItems: "baseline",
                             gap: 8, marginTop: 4 }}>
                <span style={{ color: COL.text, fontSize: 20, fontWeight: 700 }}>
                  {fmtVal(m.a)}
                </span>
                <span style={{ color: COL.dim, fontSize: 11 }}>{m.u}</span>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 4,
                             fontSize: 11 }}>
                <span style={{ color: COL.dim }}>from {fmtVal(m.b)}</span>
                <span style={{ color: delta_pos ? COL.green : COL.red,
                                 fontWeight: 600 }}>
                  {delta_pos ? "▼" : "▲"} {m.d}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 20 }}>
        <div style={sectionTitle}>Safety deltas · must be ≥ 0</div>
        {[
          ["Structural safety", D.structural_safety_delta],
          ["Fire safety", D.fire_safety_delta],
        ].map(([lbl, v]) => (
          <div key={lbl} style={rowStyle}>
            <Shield size={13} style={{ color: v >= 0 ? COL.green : COL.red }} />
            <span style={{ flex: 1 }}>{lbl}</span>
            <span style={{ color: v >= 0 ? COL.green : COL.red,
                             fontWeight: 700 }}>
              {v >= 0 ? "✓" : "✗"}  Δ {v}
            </span>
          </div>
        ))}
      </div>

      {redesign.note && (
        <div style={{ marginTop: 16, padding: 12, background: COL.surface,
                        border: `1px solid ${COL.amber}`,
                        borderRadius: 6, fontSize: 11, color: COL.dim }}>
          <Info size={12} style={{ marginRight: 6, verticalAlign: -1,
                                     color: COL.amber }} />
          {redesign.note}
        </div>
      )}

      {/* RG7 · 3D split-view Before/After */}
      <div data-testid="dr-3d-split-view"
            style={{ marginTop: 24, display: "grid",
                     gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {[
          { key: "before", label: "BEFORE — original design",
              accent: COL.dim },
          { key: "after", label: "AFTER — optimized design",
              accent: COL.green },
        ].map(pane => (
          <div key={pane.key}
                data-testid={`dr-3d-pane-${pane.key}`}
                style={{ ...cardStyle, borderLeft:
                          `3px solid ${pane.accent}` }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700,
                              color: pane.accent }}>{pane.label}</span>
              <Boxes size={14} style={{ color: pane.accent }} />
            </div>
            <div style={{ height: 220, background: COL.surface,
                            display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center",
                            border: `1px dashed ${COL.border}`,
                            borderRadius: 4, color: COL.dim,
                            fontSize: 11, textAlign: "center", padding: 12 }}>
              <Boxes size={32}
                     style={{ color: pane.accent, marginBottom: 10 }} />
              <div style={{ fontSize: 12, color: COL.text,
                              marginBottom: 4 }}>
                {pane.key === "before" ? "Original IFC ready"
                                         : "Optimized IFC ready"}
              </div>
              <div style={{ fontSize: 10, marginBottom: 12 }}>
                Download and open in any IFC viewer (Solibri /
                &nbsp;Navisworks / BIMcollab).
              </div>
              <a href={pane.key === "before"
                       ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/projects/${projectId}/design-review/download/ifc?variant=baseline`
                       : `${process.env.REACT_APP_BACKEND_URL || ""}/api/projects/${projectId}/design-review/download/ifc?variant=optimized`}
                  target="_blank" rel="noreferrer"
                  data-testid={`dr-3d-ifc-${pane.key}`}
                  style={{ padding: "6px 12px", background: pane.accent,
                            color: "#020617", borderRadius: 3,
                            textDecoration: "none", fontSize: 11,
                            fontWeight: 600, display: "inline-flex",
                            alignItems: "center", gap: 4 }}>
                <Download size={11} />
                Download {pane.key === "before" ? "baseline" : "optimized"} IFC
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── BIM & Rerun Pane (Wave-3) ────────────────────────── */

function BimPane({ projectId, accepted, suggestions, impactPreview,
                    orchestrator, onRefresh, onRestoreBaseline, onDownload,
                    rvtInputRef, onRvtUpload, onRvtPoll, rvtJobs,
                    restoredMsg, busy }) {
  const affected = impactPreview?.stages_impacted || [];
  const orchStatus = orchestrator?.status || "not_started";
  const reran = orchestrator?.reran || {};
  const totalRerun = Object.keys(reran).length;
  const okRerun = Object.values(reran).filter(r => r?.status === "ok").length;
  const failedRerun = Object.values(reran).filter(r => r?.status === "failed").length;
  const scheduledStages = orchestrator?.affected_stages || affected;
  const progressPct = scheduledStages.length > 0
    ? Math.round((totalRerun / scheduledStages.length) * 100)
    : 0;

  return (
    <div data-testid="dr-bim-pane">
      {/* Impact preview */}
      <div style={{ display: "grid",
                       gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={bimCardStyle} data-testid="dr-bim-impact-preview">
          <div style={bimCardHeader}>
            <ChevronRight size={14} style={{ color: COL.spark }} />
            <span>IMPACT PREVIEW — stages that will rerun</span>
          </div>
          <div style={{ padding: 12, fontSize: 11 }}>
            <div style={{ color: COL.dim, marginBottom: 6 }}>
              {accepted.size} suggestion(s) accepted · {affected.length}
              &nbsp;stage(s) impacted
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                 data-testid="dr-bim-impacted-stages">
              {affected.length > 0 ? affected.map(s => (
                <span key={s}
                       data-testid={`dr-bim-stage-chip-${s}`}
                       style={{ background: COL.surface,
                                border: `1px solid ${COL.border}`,
                                borderRadius: 3, padding: "3px 8px",
                                fontSize: 10, color: COL.text }}>{s}</span>
              )) : (
                <span style={{ color: COL.dim, fontSize: 11 }}>
                  Accept suggestions on the Optimize tab to see the
                  affected stages.
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Orchestrator status */}
        <div style={bimCardStyle} data-testid="dr-bim-orchestrator">
          <div style={bimCardHeader}>
            <Cog size={14} style={{ color: COL.spark }} />
            <span>ORCHESTRATOR — full Stage B–I rerun</span>
            <button data-testid="dr-bim-refresh"
                     onClick={onRefresh}
                     style={{ ...ghostBtn, marginLeft: "auto",
                              padding: "3px 8px", fontSize: 10 }}>
              <RefreshCcw size={10} /> refresh
            </button>
          </div>
          <div style={{ padding: 12, fontSize: 11 }}>
            <div style={{ marginBottom: 8 }}>
              Status: <BimStatusBadge status={orchStatus} />
            </div>
            {scheduledStages.length > 0 && (
              <>
                <div style={{ background: COL.surface,
                                border: `1px solid ${COL.border}`,
                                borderRadius: 3, height: 10, overflow: "hidden",
                                marginBottom: 6 }}>
                  <div data-testid="dr-bim-progress-bar"
                        style={{ background: COL.spark, height: "100%",
                                 width: `${progressPct}%`,
                                 transition: "width 0.5s ease" }} />
                </div>
                <div style={{ color: COL.dim, fontSize: 10 }}
                     data-testid="dr-bim-progress-text">
                  {totalRerun} / {scheduledStages.length} stages ·
                  &nbsp;{okRerun} ok · {failedRerun} failed · {progressPct}%
                </div>
              </>
            )}
            {orchStatus === "not_started" && (
              <div style={{ color: COL.dim, fontSize: 11 }}>
                Trigger a Redesign to start the full Stage B–I rerun.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-stage rerun table */}
      {totalRerun > 0 && (
        <div style={{ ...bimCardStyle, marginTop: 20 }}
             data-testid="dr-bim-rerun-table">
          <div style={bimCardHeader}>
            <FileText size={14} style={{ color: COL.spark }} />
            <span>PER-STAGE RESULTS</span>
          </div>
          <table style={{ width: "100%", fontSize: 11,
                            borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: COL.surface }}>
                <th style={bimThStyle}>Stage</th>
                <th style={bimThStyle}>Status</th>
                <th style={bimThStyle}>Deliverables</th>
                <th style={bimThStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(reran).map(([code, res]) => (
                <tr key={code}
                    data-testid={`dr-bim-stage-row-${code}`}
                    style={{ borderTop: `1px solid ${COL.border}` }}>
                  <td style={bimTdStyle}>{code}</td>
                  <td style={bimTdStyle}>
                    <span style={{ color: res?.status === "ok" ? COL.green
                                          : res?.status === "failed" ? COL.red
                                          : COL.dim }}>
                      {(res?.status || "?").toUpperCase()}
                    </span>
                  </td>
                  <td style={bimTdStyle}>{res?.deliverable_count ?? "—"}</td>
                  <td style={{ ...bimTdStyle, color: COL.dim,
                                 maxWidth: 320, overflow: "hidden",
                                 textOverflow: "ellipsis" }}>
                    {res?.error || res?.note || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Actions row */}
      <div style={{ display: "flex", gap: 12, marginTop: 20,
                       flexWrap: "wrap" }}>
        <button data-testid="dr-bim-download-ifc"
                 onClick={() => onDownload("ifc")}
                 style={primaryBtn}>
          <Download size={12} /> Download IFC (3D BIM)
        </button>
        <button data-testid="dr-bim-restore-baseline"
                 disabled={busy === "restore"}
                 onClick={onRestoreBaseline}
                 style={{ ...ghostBtn, display: "inline-flex",
                          alignItems: "center", gap: 4,
                          borderColor: COL.amber, color: COL.amber }}>
          <RotateCcw size={12} /> Restore baseline
        </button>
      </div>
      {restoredMsg && (
        <div data-testid="dr-bim-restore-msg"
             style={{ marginTop: 10, padding: 8, fontSize: 11,
                      color: COL.green,
                      background: COL.surface,
                      border: `1px solid ${COL.green}`, borderRadius: 4 }}>
          <CheckCircle2 size={12} style={{ marginRight: 6, verticalAlign: -1 }} />
          {restoredMsg}
        </div>
      )}

      {/* Revit .rvt ingest */}
      <div style={{ ...bimCardStyle, marginTop: 24 }}
           data-testid="dr-bim-revit-panel">
        <div style={bimCardHeader}>
          <Cloud size={14} style={{ color: COL.spark }} />
          <span>REVIT (.rvt) INGEST — Autodesk APS Model Derivative</span>
        </div>
        <div style={{ padding: 14, fontSize: 11 }}>
          <div style={{ color: COL.dim, marginBottom: 10 }}>
            Upload a .rvt file — Capexara Process Industry submits it to Autodesk Platform
            Services for SVF2 translation and metadata extraction. Job
            processing runs in the background; poll each job below.
          </div>
          <input type="file" ref={rvtInputRef} accept=".rvt"
                 multiple hidden
                 data-testid="dr-bim-rvt-file-input"
                 onChange={e => onRvtUpload([...e.target.files])} />
          <button data-testid="dr-bim-rvt-upload"
                   disabled={busy === "rvt"}
                   onClick={() => rvtInputRef.current?.click()}
                   style={primaryBtn}>
            {busy === "rvt" ? <Loader2 size={12} className="animate-spin" />
                             : <Upload size={12} />}
            &nbsp;Upload .rvt
          </button>
          {rvtJobs.length > 0 && (
            <table style={{ width: "100%", marginTop: 14, fontSize: 11,
                              borderCollapse: "collapse" }}
                   data-testid="dr-bim-rvt-jobs">
              <thead>
                <tr style={{ background: COL.surface }}>
                  <th style={bimThStyle}>Filename</th>
                  <th style={bimThStyle}>Status</th>
                  <th style={bimThStyle}>Progress</th>
                  <th style={bimThStyle}>URN</th>
                  <th style={bimThStyle}></th>
                </tr>
              </thead>
              <tbody>
                {rvtJobs.map(j => (
                  <tr key={j.urn || j.filename}
                      style={{ borderTop: `1px solid ${COL.border}` }}
                      data-testid={`dr-bim-rvt-row-${j.urn || j.filename}`}>
                    <td style={bimTdStyle}>{j.filename}</td>
                    <td style={bimTdStyle}>
                      <span style={{ color: j.status === "success" ? COL.green
                                            : j.status === "submitted" ? COL.amber
                                            : j.status === "inprogress" ? COL.amber
                                            : COL.dim }}>
                        {(j.status || "?").toUpperCase()}
                      </span>
                    </td>
                    <td style={bimTdStyle}>{j.progress_pct ?? 0}%</td>
                    <td style={{ ...bimTdStyle, color: COL.dim, maxWidth: 220,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis" }}
                        title={j.error || j.urn || ""}>
                      {j.error
                        ? <span style={{ color: COL.red }}>
                            {String(j.error).substring(0, 40)}
                            {String(j.error).length > 40 ? "…" : ""}
                          </span>
                        : (j.urn ? j.urn.substring(0, 24) + "…" : "—")}
                    </td>
                    <td style={bimTdStyle}>
                      {j.urn && (
                        <button
                          data-testid={`dr-bim-rvt-poll-${j.urn}`}
                          onClick={() => onRvtPoll(j.urn)}
                          style={{ ...ghostBtn, padding: "3px 8px",
                                   fontSize: 10 }}>
                          <RefreshCcw size={10} /> Poll
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const bimCardStyle = { background: COL.bg, border: `1px solid ${COL.border}`,
  borderRadius: 6, overflow: "hidden" };
const bimCardHeader = { display: "flex", alignItems: "center", gap: 6,
  padding: "10px 12px", background: COL.surface, borderBottom:
    `1px solid ${COL.border}`, fontSize: 11, fontWeight: 700,
  color: COL.text };

function BimStatusBadge({ status }) {
  const map = {
    complete: { bg: COL.green, text: "COMPLETE" },
    in_progress: { bg: COL.amber, text: "IN PROGRESS" },
    failed: { bg: COL.red, text: "FAILED" },
    not_started: { bg: COL.dim, text: "NOT STARTED" },
    scheduled: { bg: COL.amber, text: "SCHEDULED" },
  };
  const s = map[status] || map.not_started;
  return (
    <span data-testid={`dr-bim-status-${status}`}
           style={{ background: s.bg, color: "#020617",
                    padding: "2px 8px", borderRadius: 3, fontSize: 10,
                    fontWeight: 700 }}>{s.text}</span>
  );
}
const bimThStyle = { textAlign: "left", padding: "8px 12px", color: COL.dim,
  fontSize: 10, fontWeight: 700, textTransform: "uppercase" };
const bimTdStyle = { padding: "8px 12px", fontSize: 11 };


/* ── styles ─────────────────────────────────────── */

function tabBtn(active) {
  return { background: active ? COL.spark : "transparent",
    color: active ? "#020617" : COL.dim,
    border: `1px solid ${active ? COL.spark : COL.border}`,
    padding: "6px 12px", borderRadius: 4, cursor: "pointer",
    fontSize: 11, fontWeight: 700, ...mono };
}
const ghostBtn = { background: "transparent", color: COL.text,
  border: `1px solid ${COL.border}`, padding: "6px 10px", borderRadius: 4,
  cursor: "pointer", ...mono };
const primaryBtn = { background: COL.spark, color: "#020617", border: 0,
  padding: "8px 16px", borderRadius: 4, cursor: "pointer", fontWeight: 700,
  fontSize: 12, ...mono, display: "inline-flex", alignItems: "center",
  gap: 4 };
const secondaryBtn = { background: "transparent", color: COL.spark,
  border: `1px solid ${COL.spark}`, padding: "8px 14px", borderRadius: 4,
  cursor: "pointer", fontSize: 12, ...mono, display: "inline-flex",
  alignItems: "center", gap: 4 };
const dropStyle = { display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center", padding: 40,
  border: `2px dashed ${COL.spark}55`, borderRadius: 8,
  background: "rgba(34,211,238,0.03)" };
const sectionTitle = { fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
  color: COL.dim, marginBottom: 8, textTransform: "uppercase" };
const rowStyle = { display: "flex", alignItems: "center", gap: 10,
  padding: "8px 10px", borderBottom: `1px solid ${COL.border}`, fontSize: 12 };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 11 };
const thStyle = { textAlign: "left", padding: "8px 10px", color: COL.dim,
  fontWeight: 700, borderBottom: `1px solid ${COL.border}`, fontSize: 10 };
const tdStyle = { padding: "8px 10px", verticalAlign: "top",
  borderBottom: `1px solid ${COL.border}22` };
const cardStyle = { padding: 12, borderRadius: 6, background: COL.surface,
  border: `1px solid ${COL.border}`, marginBottom: 10 };
function scorePill(pct) {
  const bg = pct >= 80 ? COL.green : pct >= 60 ? COL.amber : COL.red;
  return { background: `${bg}22`, color: bg, padding: "4px 10px",
           borderRadius: 12, fontSize: 11, fontWeight: 700 };
}
function statusPill(status) {
  const bg = status === "PASS" ? COL.green : status === "FAIL" ? COL.red :
             COL.amber;
  return { background: `${bg}22`, color: bg, padding: "2px 8px",
           borderRadius: 10, fontSize: 10, fontWeight: 700 };
}
function severityPill(sev) {
  const bg = sev === "critical" ? COL.red : sev === "major" ? COL.amber :
             sev === "minor" ? COL.spark : COL.dim;
  return { background: `${bg}22`, color: bg, padding: "2px 8px",
           borderRadius: 10, fontSize: 10, fontWeight: 700 };
}
function categoryPill(cat) {
  return { background: `${COL.spark}22`, color: COL.spark,
           padding: "2px 8px", borderRadius: 10, fontSize: 10,
           fontWeight: 700 };
}
function filterBtn(active) {
  return { background: active ? COL.spark : "transparent",
           color: active ? "#020617" : COL.dim, border: `1px solid ${COL.border}`,
           padding: "3px 8px", borderRadius: 3, cursor: "pointer",
           fontSize: 10, ...mono };
}
function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
  return String(v);
}
function EmptyCTA({ text, testid }) {
  return (
    <div data-testid={testid} style={{ textAlign: "center", padding: 40,
                                          color: COL.dim, fontSize: 13 }}>
      <Info size={22} style={{ color: COL.spark, opacity: 0.6,
                                 marginBottom: 8 }} />
      <div>{text}</div>
    </div>
  );
}
